/**
 * @fileoverview Hook to fetch app access configuration.
 * Returns the current access mode (open/password/closed) and optional password.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type AppAccessMode = 'open' | 'password' | 'closed';

interface AppAccessConfig {
  mode: AppAccessMode;
  password: string | null;
}

export function useAppAccessConfig() {
  const { data, isLoading } = useQuery({
    queryKey: ['app-access-config'],
    queryFn: async (): Promise<AppAccessConfig> => {
      const { data, error } = await (supabase as any)
        .from('app_access_config')
        .select('mode, password')
        .eq('id', 'default')
        .single();

      if (error) throw error;
      return { mode: data.mode as AppAccessMode, password: data.password };
    },
    staleTime: 1000 * 60 * 2, // 2 min cache
    gcTime: 1000 * 60 * 5,
  });

  return {
    mode: data?.mode ?? 'open',
    password: data?.password ?? null,
    isLoading,
  };
}
